import React, { useEffect, useState } from 'react';
import ReactConfetti from 'react-confetti';

import PropTypes from 'prop-types';

import { isServer } from '@ulta/core/utils/device_detection/device_detection';

/**
 * Confetti component is a wrapper around react-confetti to display a confetti animation.
 * It defaults to using the window dimensions for a full-screen effect but can be
 * constrained by providing width and height props.
 *
 * @module views/components/Confetti
 * @memberof -Core
 */

const FALLBACK_DEFAULT_COLORS = ['#F26B1C', '#B90B70'];

/**
 * Draws a 12x11 rectangle shape for a confetti piece, centered at its origin.
 * @param {CanvasRenderingContext2D} context The canvas rendering context.
 */
export const drawRect = ( context ) => {
  context.fillRect( -6, -5.5, 12, 11 );
};

/**
   * Helper function to safely get CSS variable values.
   * Runs only on the client-side where `document` is available.
   * @param {string} variableName - The name of the CSS variable.
   * @returns {string} The value of the CSS variable or an empty string if not found/not in browser.
*/
export const getCssVariableValue = ( variableName ) => {
  if( !isServer() ){
    return getComputedStyle( document.documentElement ).getPropertyValue( variableName ).trim();
  }
  return '';
};
const Confetti = ( props ) => {
  const {
    width,
    height,
    run = Confetti.defaultProps.run,
    recycle = Confetti.defaultProps.recycle,
    numberOfPieces = Confetti.defaultProps.numberOfPieces,
    colors: propColors,
    drawShape = Confetti.defaultProps.drawShape,
    ...restProps
  } = props;

  const [currentConfettiColors, setCurrentConfettiColors] = useState( FALLBACK_DEFAULT_COLORS );

  useEffect( () => {
    let resolvedColors = [];

    if( propColors && propColors.length > 0 ){
      // If colors are passed via props, process them (resolve CSS variables if necessary).
      resolvedColors = propColors.map( colorString => {
        if( typeof colorString === 'string' && colorString.startsWith( '--' ) ){
          return getCssVariableValue( colorString );
        }
        return colorString;
      } ).filter( color => typeof color === 'string' && color.trim() !== '' ); // Filter out empty/invalid strings
    }

    // If propColors did not yield any valid colors (e.g., not provided, empty, or all unresolvable),
    // use the hardcoded fallback colors. Otherwise, use the resolved propColors.
    if( resolvedColors.length === 0 ){
      setCurrentConfettiColors( FALLBACK_DEFAULT_COLORS );
    }
    else {
      setCurrentConfettiColors( resolvedColors );
    }

  }, [propColors] );

  // react-confetti will not render if run and recycle are false (after initial burst) or no width and height
  if( ( !run && !recycle ) || !width || !height ){
    return null;
  }

  return (
    <ReactConfetti
      width={ width }
      height={ height }
      run={ run }
      recycle={ recycle }
      numberOfPieces={ numberOfPieces }
      colors={ currentConfettiColors }
      drawShape={ drawShape }
      { ...restProps }
    />
  );
};


Confetti.propTypes = {
  /** Width of the confetti canvas. Defaults to window width. */
  width: PropTypes.number,
  /** Height of the confetti canvas. Defaults to window height. */
  height: PropTypes.number,
  /** Whether the confetti animation should be running. */
  run: PropTypes.bool,
  /** Whether the confetti should recycle. */
  recycle: PropTypes.bool,
  /** Number of confetti pieces. */
  numberOfPieces: PropTypes.number,
  /** Array of colors for confetti pieces. */
  colors: PropTypes.arrayOf( PropTypes.string ),
  /** Gravity applied to confetti pieces. */
  gravity: PropTypes.number,
  /** Wind applied to confetti pieces. */
  wind: PropTypes.number,
  /** Opacity of confetti pieces. */
  opacity: PropTypes.number,
  /** Duration of the tween animation for each piece. */
  tweenDuration: PropTypes.number,
  /** Function to draw custom shapes for confetti. */
  drawShape: PropTypes.func,
  /** Callback function when confetti animation completes (if recycle is false). */
  onConfettiComplete: PropTypes.func
};

Confetti.defaultProps = {
  run: true,
  recycle: true,
  numberOfPieces: 200,
  drawShape: drawRect
};

export default Confetti;